import { INTEGRATION_TYPES } from 'app/pipeline-details/PipelineIntegrationsSection/PipelineIntegrations/PipelineIntegrationsTable/buildTableData';

import { PlayCircleIcon, PauseCircleIcon } from '@metarouter-private/mr-mui';

import { usePipelineIntegrationModalContext } from 'app/pipeline-details/PipelineIntegrationsSection/PipelineIntegrations/PipelineIntegrationsTable/context/pipeline-integration-modal-context';
import INTEGRATION_ACTIVATION_MODAL_NAME from 'app/pipeline-details/PipelineIntegrationsSection/PipelineIntegrations/PipelineIntegrationsTable/IntegrationActivationModal/INTEGRATION_ACTIVATION_MODAL_NAME';

import buildIntegrationStatusDetails from './buildIntegrationStatusDetails';

const TOOLTIP_MESSAGES = {
  STAGED_FOR_DELETE: 'Integration is staged for deletion',
  NOT_DEPLOYED: 'Non-deployed integrations cannot be disabled',
  DISABLED: 'Enable Integration',
  ENABLED: 'Disable Integration',
};

const DATA_TEST_IDS = {
  DISABLED: 'enableIntegrationButton',
  ENABLED: 'disableIntegrationButton',
};

export default function useActivationActionsConfig(integrationRow) {
  const { onOpenModal } = usePipelineIntegrationModalContext();

  // Don't show action buttons if the integration is unmanaged
  const isUnmanagedIntegration = integrationRow.type === INTEGRATION_TYPES.UNMANAGED;
  if (isUnmanagedIntegration) {
    return {
      isUnmanagedIntegration: true,
    };
  }

  const onClick = () => onOpenModal(INTEGRATION_ACTIVATION_MODAL_NAME, integrationRow);

  const { isDeployed, isEnabled, isStagedForDelete } = buildIntegrationStatusDetails(integrationRow);

  // Button is disabled if the integration is not deployed or if it's staged for delete
  const buttonDisabled = Boolean(!isDeployed || isStagedForDelete);

  const actionIconButtonProps = {
    tooltipMessage: buildTooltipMessage({ isDeployed, isEnabled, isStagedForDelete }),
    disabled: buttonDisabled,
    dataTestId: buildDataTestID({ isEnabled }),
    onClick: !buttonDisabled ? onClick : undefined,
  };

  const iconConfig = {
    Icon: isEnabled ? PauseCircleIcon : PlayCircleIcon,
    iconColor: buttonDisabled ? 'disabled' : 'action',
  };

  return {
    actionIconButtonProps,
    iconConfig,
  };
}

// Generates the Action Icon Button's tooltip based on the pipeline integration's status
function buildTooltipMessage({ isDeployed, isEnabled, isStagedForDelete }) {
  if (isStagedForDelete) return TOOLTIP_MESSAGES.STAGED_FOR_DELETE;

  if (!isDeployed) return TOOLTIP_MESSAGES.NOT_DEPLOYED;

  if (!isEnabled) return TOOLTIP_MESSAGES.DISABLED;

  // Implied: Integration is active
  return TOOLTIP_MESSAGES.ENABLED;
}

// Generates the Action Icon Button's data-test-id based on the pipeline integration's disabled status
function buildDataTestID({ isEnabled }) {
  switch (isEnabled) {
    case true:
      return DATA_TEST_IDS.ENABLED;
    case false:
      return DATA_TEST_IDS.DISABLED;
    default:
      return undefined;
  }
}
